import { useDispatch, useSelector } from 'react-redux';
import {
  CircularProgress,
  FormControl, Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select
} from '@mui/material';
import PaginationTable from './Pagination';
import { Fragment, useContext, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import {
  apiVideoLibraryProvider,
  requestOrganisationLanguage,
  VideoLibState
} from '../store/videoLibrarySlice';
import ToolBarVideoLibrary from './ToolBarVideoLibrary';
import { VideoLibraryTable } from './VideoLibraryTable';
import { OrganisationContext } from '../App';
import { VideoDetailedPreview } from './modal/VideoDetailedPreview';
import { RootState } from '../index';
import { VideoLibItem } from './types/videos';
import useWebSocket from 'react-use-websocket';
import { WebSocketMessage } from './types/websocket';
import { TranscriptStatus } from './notification/types';
import { SimpleNotification } from './notification/SimpleNotification';
import * as React from 'react';
import { VideoTranscript } from './types/videoTranscript';

const WS_URL = process.env.REACT_APP_WS_URL as string;

export default function VideoLibrary() {
  const dispatch = useDispatch();
  const { selectedOrganisation } = useContext(OrganisationContext);

  const {
    searchText,
    videoLibraryFilter,
    videos,
    pagination,
    busy,
    rightsholders
  } = useSelector<RootState, VideoLibState>((state) => state.videoLibrary);

  const [videoPreview, setVideoPreview] = useState<VideoLibItem>();
  const [videoPreviewIndex, setVideoPreviewIndex] = useState<number | undefined>();
  const [searchInputText, setSearchInputText] = useState({
    searchText: '',
  });
  const [orderBy, setOrderBy] = useState<string>('created_at');
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('desc');
  const [rightsholderFilter, setRightsholderFilter] = useState<string>('');
  const [transcriptMessage, setTranscriptMessage] = useState<string>('');
  const handleClose = () => setVideoPreviewIndex(undefined);

  const updateList = (search: string) => {
    return apiVideoLibraryProvider({
      publisher: selectedOrganisation,
      limit: pagination.itemsPerPage,
      page: pagination.currentPage,
      filterByFormat: videoLibraryFilter,
      orderBy: orderBy,
      orderDirection: orderDirection,
      rightsholder: rightsholderFilter,
      search,
    });
  };

  useEffect(() => {
    if (videoPreviewIndex !== undefined) {
      setVideoPreview(videos[videoPreviewIndex]);
    } else {
      setVideoPreview(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoPreviewIndex, videos]);

  useEffect(() => {
    if (!selectedOrganisation) return;
    dispatch<any>(updateList(searchText));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, pagination.itemsPerPage, pagination.currentPage, videoLibraryFilter, selectedOrganisation, orderBy, orderDirection, rightsholderFilter]);

  useEffect(() => {
    if (!selectedOrganisation) return;
    dispatch<any>(requestOrganisationLanguage({
      publisher: selectedOrganisation,
      format: videoLibraryFilter
    }));
    return () => {
      dispatch({ type: 'RESET_VIDEO_LIBRARY' });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useWebSocket(WS_URL, {
    shouldReconnect: () => true,
    reconnectInterval: 3000,
    onMessage: async (message) => {
      const messageData = JSON.parse(message.data) as WebSocketMessage;

      if (selectedOrganisation !== messageData.data?.requester_org) {
        return;
      }

      switch (messageData.action) {
        case 'video_transcript': {
          const status = messageData.data as TranscriptStatus;
          if (status.status === 'completed') {
            setTranscriptMessage('Translation completed.');
            dispatch<any>(updateList(searchText));
            setTimeout(() => {
              setTranscriptMessage('');
            }, 5000);
          } else if (status.status === 'requested') {
            setTranscriptMessage('Translation requested..')
          } else if (status.status === 'processing') {
            setTranscriptMessage('Translation processing..')
          }
          break;
        }
        default:
          // no op
      }
    }
  });

  useEffect(() => {
    if (videoPreview) {
      let transcripts: VideoTranscript[] = [];

      for (const video of videos) {
        if (video.uuid === videoPreview.uuid) {
          transcripts = video.transcripts;
          break;
        }
      }

      if (transcripts.length > 0) {
        setVideoPreview({
          ...videoPreview,
          transcripts,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos]);

  return videos ? (
    <Fragment>
      {videoPreview && (
        <VideoDetailedPreview
          open={Boolean(videoPreview)}
          handleClose={handleClose}
          updateVideoCb={() => {
            dispatch<any>(updateList(searchText));
            if (videoPreviewIndex !== undefined) {
              setVideoPreview(videos[videoPreviewIndex]);
            }
          }}
          video={{
            media_id: videoPreview.media_id,
            summary: videoPreview.summary,
            rightsholder: videoPreview.rightsholder,
            video_type: videoPreview.video_type,
            format: videoPreview.format,
            preview_uri: videoPreview.preview_uri,
            title: videoPreview.title,
            publish_date: new Date(videoPreview.publish_date),
            parent_media_id: videoPreview.parent_media_id,
            parent_title: videoPreview.parent_title,
            parent_title_translated: videoPreview.parent_title_translated,
            has_variant: videoPreview.has_variant,
            can_manage_video: videoPreview.canManageVideo,
            transcripts: videoPreview.transcripts,
            uuid: videoPreview.uuid,
            disable_custom_caption: videoPreview.disable_custom_caption,
            opus_title: videoPreview.opus_title,
            opus_description: videoPreview.opus_description,
            opus_clip_genre: videoPreview.opus_clip_genre,
            opus_hashtags: videoPreview.opus_hashtags,
            asa_compliant: videoPreview.asa_compliant,
            creator_url: videoPreview.creator_channel_url,
            creator_name: videoPreview.creator_name,
            creator_logo_url: videoPreview.creator_logo_url,
            use_org_meta: videoPreview.use_org_meta
          }}
        />
      )}

      <ToolBarVideoLibrary
        setSearchInputText={setSearchInputText}
        searchInputText={searchInputText}
        defaultValue={'vertical'}
      />

      <Box sx={{ position: 'relative' }}>
        {busy &&
          <LinearProgress sx={{ position: 'absolute', top: 0, width: '100%' }} color="success"/>}

        <Grid container justifyContent={'flex-end'} sx={{ padding: '10px 0' }} spacing={2}
              alignItems={'center'}>
          <Grid item xs={2}>
            <FormControl fullWidth size="small">
              <InputLabel size="small">Filter by Rightsholder</InputLabel>
              <Select
                size={'small'}
                value={rightsholderFilter}
                label="Filter by Rightsholder"
                onChange={(e) => setRightsholderFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {rightsholders.map(holder => (
                  <MenuItem key={holder} value={holder}>{holder}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <VideoLibraryTable
          videos={videos}
          onSetVideoPreview={setVideoPreviewIndex}
          setOrderDirection={setOrderDirection}
          setOrderBy={setOrderBy}
          orderBy={orderBy}
          orderDirection={orderDirection}
        />
      </Box>
      {busy && <LinearProgress color="success"/>}
      <PaginationTable
        defaultSize={20}
        busy={busy}
        startPage={pagination.currentPage - 1}
        count={pagination.totalItems}
        onChange={page =>
          dispatch({
            type: 'SET_PAGINATION',
            payload: {
              currentPage: page + 1,
            },
          })
        }
        updateRowPerPage={totalPerPage => {
          dispatch({
            type: 'SET_PAGINATION',
            payload: {
              currentPage: 1,
              itemsPerPage: totalPerPage,
            },
          });
        }}
      />
      <SimpleNotification message={transcriptMessage}/>
    </Fragment>
  ) : (
    <CircularProgress style={{ padding: '5px' }}/>
  );
}
